import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useRouter } from 'vue-router';
import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { getAuthenticated } from '@/util/requests';

type Navigation = {
  name: string;
  params: Record<string, unknown>;
  query?: Record<string, string>;
};

const DEFAULT_NAVIGATION: Navigation = {
  name: 'Dashboard',
  params: {},
  query: {},
};

export const useAuthStore = defineStore(
  'auth',
  () => {
    // Router and route instances for navigation management
    const router = useRouter();

    const authError = ref('');
    const previousNavigation = ref<Navigation>(DEFAULT_NAVIGATION);
    const tokenExpiresAt = ref<number>();

    /**
     * Sets the previous navigation route and its parameters.
     * This is used to redirect users back to their last visited page after authentication.
     * @param to - The route location object of the previous navigation.
     */
    function setPreviousNavigation(to: RouteLocationNormalizedGeneric) {
      const name = to.name as string;
      const params = to.params as Record<string, unknown>;
      const query = to.query as Record<string, string>;

      if (!router.hasRoute(name)) {
        console.warn(`Route ${name} does not exist. Defaulting to Dashboard.`);
        return resetPreviousNavigation();
      }
      // If the route is the landing page, reset to Dashboard
      if (to.name === 'LandingPage') return resetPreviousNavigation();
      previousNavigation.value = { name, params, query };
    }

    /**
     * Resets the previous navigation state.
     * This is typically called when the user logs out or when the authentication state changes.
     */
    function resetPreviousNavigation() {
      previousNavigation.value = DEFAULT_NAVIGATION;
    }

    function setAuthError(error: string) {
      authError.value = error;
    }

    function setTokenExpiresAt(exp: number) {
      tokenExpiresAt.value = exp;
    }

    function clearAuthError() {
      authError.value = '';
    }

    function getAndClearAuthError() {
      const error = authError.value;
      clearAuthError();
      return error;
    }

    /**
     * Checks if the user is authenticated.
     * 1. If the token expiration time is not set, it makes a request to verify authentication.
     * 2. If the token expiration time is set, it checks if the token is still valid based on the current time.
     * @returns A promise that resolves to true if the user is authenticated, false otherwise.
     */
    async function isAuthenticated(): Promise<boolean> {
      const exp = tokenExpiresAt.value;
      if (!exp) {
        const response = await getAuthenticated();
        if (!response.success) return false;
        const { exp } = response;
        setTokenExpiresAt(exp);
        return true;
      }

      // check if the token is expired
      const currentTime = Math.floor(Date.now() / 1000);
      return exp > currentTime;
    }

    return {
      authError,
      setAuthError,
      getAndClearAuthError,
      isAuthenticated,
      // Navigation management
      previousNavigation,
      resetPreviousNavigation,
      setPreviousNavigation,
    };
  },
  {
    persist: [
      {
        pick: ['previousNavigation', 'tokenExpiresAt'],
        storage: localStorage,
      },
      {
        pick: ['authError'],
        storage: sessionStorage,
      },
    ],
  },
);

export default useAuthStore;
